import React from 'react';
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

// Color palette for charts
const COLORS = {
    primary: '#f97316', // Orange
    secondary: '#3b82f6', // Blue
    success: '#22c55e', // Green
    warning: '#eab308', // Yellow
    danger: '#ef4444', // Red
    purple: '#a855f7', // Purple
    cyan: '#06b6d4', // Cyan
    pink: '#ec4899', // Pink
};

const PIE_COLORS = [COLORS.primary, COLORS.secondary, COLORS.success, COLORS.purple, COLORS.warning];

interface ChartDataPoint {
    name: string;
    value?: number;
    [key: string]: string | number | undefined;
}

interface EarningsChartProps {
    type: 'line' | 'area' | 'bar' | 'pie';
    data: ChartDataPoint[];
    title?: string;
    description?: string;
    dataKeys?: string[];
    colors?: string[];
    height?: number;
    showLegend?: boolean;
    showGrid?: boolean;
    gradientFill?: boolean;
    animated?: boolean;
    className?: string;
    formatValue?: (value: number) => string;
}

const formatCurrency = (value: number) => {
    if (value >= 100000) {
        return `₹${(value / 100000).toFixed(1)}L`;
    } else if (value >= 1000) {
        return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value}`;
};

const CustomTooltip = ({ active, payload, label, formatValue }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                <p className="font-medium text-sm text-gray-900 dark:text-white mb-1">{label}</p>
                {payload.map((entry: any, index: number) => (
                    <p key={index} className="text-sm" style={{ color: entry.color }}>
                        {entry.name}: {formatValue ? formatValue(entry.value) : formatCurrency(entry.value)}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

export function EarningsLineChart({
    data,
    dataKeys = ['value'],
    colors = [COLORS.primary],
    height = 300,
    showGrid = true,
    showLegend = true,
    gradientFill = false,
    formatValue = formatCurrency,
}: Omit<EarningsChartProps, 'type'>) {
    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                {gradientFill && (
                    <defs>
                        {dataKeys.map((key, index) => (
                            <linearGradient key={key} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={colors[index % colors.length]} stopOpacity={0.8} />
                                <stop offset="95%" stopColor={colors[index % colors.length]} stopOpacity={0.1} />
                            </linearGradient>
                        ))}
                    </defs>
                )}
                {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
                <XAxis dataKey="name" stroke="#6b7280" fontSize={12} tickLine={false} />
                <YAxis stroke="#6b7280" fontSize={12} tickLine={false} tickFormatter={formatValue} />
                <Tooltip content={<CustomTooltip formatValue={formatValue} />} />
                {showLegend && <Legend />}
                {dataKeys.map((key, index) => (
                    <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={colors[index % colors.length]}
                        strokeWidth={2}
                        dot={{ fill: colors[index % colors.length], strokeWidth: 2 }}
                        activeDot={{ r: 6, fill: colors[index % colors.length] }}
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
}

export function EarningsAreaChart({
    data,
    dataKeys = ['value'],
    colors = [COLORS.primary],
    height = 300,
    showGrid = true,
    showLegend = true,
    formatValue = formatCurrency,
}: Omit<EarningsChartProps, 'type'>) {
    return (
        <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <defs>
                    {dataKeys.map((key, index) => (
                        <linearGradient key={key} id={`gradient-area-${key}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={colors[index % colors.length]} stopOpacity={0.4} />
                            <stop offset="95%" stopColor={colors[index % colors.length]} stopOpacity={0.05} />
                        </linearGradient>
                    ))}
                </defs>
                {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
                <XAxis dataKey="name" stroke="#6b7280" fontSize={12} tickLine={false} />
                <YAxis stroke="#6b7280" fontSize={12} tickLine={false} tickFormatter={formatValue} />
                <Tooltip content={<CustomTooltip formatValue={formatValue} />} />
                {showLegend && <Legend />}
                {dataKeys.map((key, index) => (
                    <Area
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={colors[index % colors.length]}
                        strokeWidth={2}
                        fill={`url(#gradient-area-${key})`}
                    />
                ))}
            </AreaChart>
        </ResponsiveContainer>
    );
}

export function EarningsBarChart({
    data,
    dataKeys = ['value'],
    colors = [COLORS.primary, COLORS.secondary, COLORS.success],
    height = 300,
    showGrid = true,
    showLegend = true,
    formatValue = formatCurrency,
}: Omit<EarningsChartProps, 'type'>) {
    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
                <XAxis dataKey="name" stroke="#6b7280" fontSize={12} tickLine={false} />
                <YAxis stroke="#6b7280" fontSize={12} tickLine={false} tickFormatter={formatValue} />
                <Tooltip content={<CustomTooltip formatValue={formatValue} />} />
                {showLegend && <Legend />}
                {dataKeys.map((key, index) => (
                    <Bar
                        key={key}
                        dataKey={key}
                        fill={colors[index % colors.length]}
                        radius={[4, 4, 0, 0]}
                    />
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
}

interface PieChartDataPoint {
    name: string;
    value: number;
}

export function EarningsPieChart({
    data,
    height = 300,
    showLegend = true,
    formatValue = formatCurrency,
}: {
    data: PieChartDataPoint[];
    height?: number;
    showLegend?: boolean;
    formatValue?: (value: number) => string;
}) {
    const renderCustomLabel = ({ name, percent }: { name: string; percent: number }) => {
        return `${name}: ${(percent * 100).toFixed(0)}%`;
    };

    return (
        <ResponsiveContainer width="100%" height={height}>
            <PieChart>
                <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={renderCustomLabel}
                    labelLine={{ stroke: '#6b7280', strokeWidth: 1 }}
                >
                    {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                </Pie>
                <Tooltip
                    formatter={(value: number) => formatValue(value)}
                    contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                />
                {showLegend && (
                    <Legend
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        formatter={(value) => <span className="text-sm text-gray-600">{value}</span>}
                    />
                )}
            </PieChart>
        </ResponsiveContainer>
    );
}

// Sparkline component for mini trend indicators
export function Sparkline({
    data,
    color = COLORS.primary,
    height = 40,
    width = 120,
}: {
    data: number[];
    color?: string;
    height?: number;
    width?: number;
}) {
    const chartData = data.map((value, index) => ({ index, value }));

    return (
        <ResponsiveContainer width={width} height={height}>
            <AreaChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                <defs>
                    <linearGradient id="sparkline-gradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <Area
                    type="monotone"
                    dataKey="value"
                    stroke={color}
                    strokeWidth={1.5}
                    fill="url(#sparkline-gradient)"
                    dot={false}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
}

// Composite chart card component
interface EarningsChartCardProps extends EarningsChartProps {
    icon?: React.ReactNode;
}

export function EarningsChartCard({
    type,
    data,
    title,
    description,
    dataKeys = ['value'],
    colors = [COLORS.primary],
    height = 300,
    showLegend = true,
    showGrid = true,
    icon,
    className = '',
    formatValue = formatCurrency,
}: EarningsChartCardProps) {
    const renderChart = () => {
        switch (type) {
            case 'line':
                return (
                    <EarningsLineChart
                        data={data}
                        dataKeys={dataKeys}
                        colors={colors}
                        height={height}
                        showGrid={showGrid}
                        showLegend={showLegend}
                        formatValue={formatValue}
                    />
                );
            case 'area':
                return (
                    <EarningsAreaChart
                        data={data}
                        dataKeys={dataKeys}
                        colors={colors}
                        height={height}
                        showGrid={showGrid}
                        showLegend={showLegend}
                        formatValue={formatValue}
                    />
                );
            case 'bar':
                return (
                    <EarningsBarChart
                        data={data}
                        dataKeys={dataKeys}
                        colors={colors}
                        height={height}
                        showGrid={showGrid}
                        showLegend={showLegend}
                        formatValue={formatValue}
                    />
                );
            case 'pie':
                return (
                    <EarningsPieChart
                        data={data as PieChartDataPoint[]}
                        height={height}
                        showLegend={showLegend}
                        formatValue={formatValue}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Card className={className}>
            <CardHeader>
                <div className="flex items-center gap-2">
                    {icon}
                    <div>
                        {title && <CardTitle>{title}</CardTitle>}
                        {description && <CardDescription>{description}</CardDescription>}
                    </div>
                </div>
            </CardHeader>
            <CardContent>{renderChart()}</CardContent>
        </Card>
    );
}

export default EarningsChartCard;
